import type { Messages } from "../../core/api";

export type PatientProfileSubsection = {
  id: string;
  label: string;
  status: "started" | "placeholder";
  isOnboardingOnly?: boolean;
  requiresBillingFeature?: boolean;
  permissionKey?:
    | Messages["AgencyMemberPermission"]
    | `${Messages["AgencyMemberPermission"]}|${Messages["AgencyMemberPermission"]}`;
};

export type PatientProfileSection = {
  label: string;
  hiddenPatientStatuses?: string[];
  subsections: PatientProfileSubsection[];
};

export const patientProfileSections: PatientProfileSection[] = [
  {
    label: "Calendar",
    subsections: [
      {
        id: "calendar",
        label: "Calendar",
        status: "started",
        permissionKey: "view_patient_page_calendar",
      },
      {
        id: "list-view",
        label: "List view",
        status: "started",
        permissionKey: "view_patient_page_calendar",
      },
      {
        id: "weekly-template",
        label: "Weekly template",
        status: "started",
        permissionKey: "view_patient_page_weekly_template",
      },
      {
        id: "caregivers",
        label: "Caregivers",
        status: "started",
        permissionKey: "view_patient_page_caregivers",
      },
    ],
  },
  {
    label: "Profile",
    subsections: [
      {
        id: "summary",
        label: "Summary",
        status: "started",
        permissionKey: "view_patient_page_summary",
      },
      {
        id: "status-review",
        label: "Status review",
        status: "started",
        permissionKey: "view_patient_page_summary",
      },
      {
        id: "staffing-preferences",
        label: "Staffing preferences",
        status: "started",
        permissionKey: "view_patient_page_summary",
      },
      {
        id: "availability",
        label: "Availability",
        status: "started",
        permissionKey: "view_patient_page_pay_rates",
      },
      {
        id: "addresses",
        label: "Addresses",
        status: "started",
        permissionKey: "view_patient_page_summary",
      },
      {
        id: "emergency-contact-preparedness",
        label: "Emergency Contact & Preparedness",
        status: "started",
        permissionKey: "view_patient_page_emergency",
      },
      {
        id: "patient-contacts",
        label: "Patient Contacts",
        status: "started",
        permissionKey: "view_patient_page_information",
      },
      {
        id: "information",
        label: "Information",
        status: "started",
        permissionKey: "view_patient_page_information",
      },
      { id: "faxes", label: "Faxes", status: "started" },
      {
        id: "patient-caregiver-relationships",
        label: "Patient-Caregiver Relationships",
        status: "started",
      },
    ],
  },
  {
    label: "Medical",
    hiddenPatientStatuses: ["DRAFT", "LOST"],
    subsections: [
      {
        id: "medical-summary",
        label: "Summary",
        status: "started",
        permissionKey: "view_patient_page_care_level",
      },
      {
        id: "plan-of-care",
        label: "Plan of Care",
        status: "placeholder",
        permissionKey: "view_patient_page_plan_of_care",
      },
      {
        id: "duty-sheet",
        label: "Duty sheet",
        status: "placeholder",
        permissionKey: "view_patient_page_duty_sheet",
      },
      {
        id: "medical-information",
        label: "Medical information",
        status: "started",
        permissionKey: "view_patient_page_medical_info",
      },
      {
        id: "physicians",
        label: "Physicians",
        status: "placeholder",
        permissionKey: "view_patient_physicians",
      },
      {
        id: "medication-profile",
        label: "Medication profile",
        status: "placeholder",
        permissionKey: "view_patient_page_medication_profile",
      },
      {
        id: "tasks",
        label: "Tasks",
        status: "placeholder",
        permissionKey: "view_patient_page_tasks" as Messages["AgencyMemberPermission"],
      },
      {
        id: "documents",
        label: "Documents",
        status: "placeholder",
        permissionKey: "view_patient_page_documents",
      },
      {
        id: "uploaded-documents",
        label: "Uploaded documents",
        status: "placeholder",
        permissionKey: "view_patient_page_uploaded_documents",
      },
      {
        id: "blood-pressure",
        label: "Blood Pressure",
        status: "placeholder",
        permissionKey: "view_patient_blood_pressure",
      },
      {
        id: "vbp",
        label: "Value-Based payments",
        status: "placeholder",
        permissionKey: "view_patient_page_vbp",
      },
      {
        id: "medical-diagnosis-codes",
        label: "Medical Diagnosis Codes",
        status: "placeholder",
        permissionKey: "view_patient_page_diagnosis_codes",
      },
      {
        id: "advanced-directives",
        label: "Advanced Directives",
        isOnboardingOnly: true,
        status: "started",
      },
    ],
  },
  {
    label: "Billing",
    subsections: [
      {
        id: "billing",
        label: "Billing",
        status: "placeholder",
        requiresBillingFeature: true,
        permissionKey: "view_billing_section_invoices",
      },
    ],
  },
  {
    label: "Administrative",
    subsections: [
      {
        id: "contracts",
        label: "Contracts",
        status: "started",
        permissionKey: "view_patient_page_contracts",
      },
      {
        id: "authorizations",
        label: "Authorizations",
        status: "placeholder",
        permissionKey: "view_patient_page_authorizations",
      },
      {
        id: "diagnosis-codes",
        label: "Diagnosis codes",
        status: "placeholder",
        permissionKey: "view_patient_page_diagnosis_codes",
      },
      {
        id: "pay-rates",
        label: "Pay rates",
        status: "placeholder",
        permissionKey: "view_patient_page_pay_rates",
      },
      {
        id: "surpluses",
        label: "Surpluses",
        status: "started",
        permissionKey: "view_patient_page_pay_rates",
      },
    ],
  },
  {
    label: "Recent activity",
    subsections: [
      {
        id: "recent-activity",
        label: "Recent activity",
        status: "started",
        permissionKey: "view_patient_page_summary",
      },
    ],
  },
];
